import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// components
import OrdersTable from '../../order/components/OrdersTable/OrdersTable';
import OrdersTopBar from './components/OrdersTopBar/OrdersTopBar';
import OrdersSidebar from './components/OrdersSidebar/OrdersSidebar';
import SearchLayout from '../../components/SearchLayout/SearchLayout';
import { ContentContainer } from './components/OrdersContent/OrdersContent.styled';

// Context
import { useAuth } from '../../contexts';

import { v4 } from 'uuid';
import listOrdersApi from '../../order/api/list.api.order';
import updateOrderApi from '../../order/api/patch.api.order';
import { getStatuses } from '../../order/constants/statuses.order.const';
import InfiniteList from '../../layout/components/InfiniteList/InfiniteList';

const PER_PAGE = 25;

const OrdersContainer = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: 'createdAt', direction: 'desc' });
  const [orders, setOrders] = useState([]);
  const statusOptions = useMemo(() => getStatuses(t), [t]);
  const [more, setMore] = useState(false);
  const [multiValue, setMultiValue] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState('mine');

  const apiIdRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    getOrders({ refresh: true, page: 1 });

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQuery = ({ 
    sortParam = sort, 
    pageParam = page, 
    multiValueParam = multiValue,
    searchParam = search,
    scopeParam = scope,
  } = {}) => {
    const userId = user?._id;
    const userDepartment = user?.department;
    
    const query = {
      limit: PER_PAGE,
      offset: (pageParam - 1) * PER_PAGE,
    };


    // Apply scope-based filtering
    if (scopeParam === 'mine') {
      query.createdBy = userId;
    } else if (scopeParam === 'department' && userDepartment) {
      query.department = userDepartment;
    }
    // For 'all' scope, don't add createdBy or department filters

    if (!!sortParam)
      query.order = `${sortParam.direction === 'asc' ? '' : '-'}${sortParam.key}`;

    if (multiValueParam && multiValueParam.length) {
      query['status__isin'] = multiValueParam.join(',');
    }

    // Add search if provided (search in order number and EDI document ID)
    if (searchParam && searchParam.trim()) {
      const searchTerm = searchParam.trim();
      query.search = `orderNumber__icontains:${searchTerm}||orderRows.documentId__icontains:${searchTerm}`;
    }

    query.populate = 'orderRows,department';
    return query;
  };

  const getOrders = async ({ 
    sortParam = sort, 
    pageParam = page, 
    searchParam = search,
    scopeParam = scope,
    multiValueParam = multiValue,
    refresh = false 
  } = {}) => {
    const API_ID = v4();
    apiIdRef.current = API_ID;

    setLoading(true);
    setSort(sortParam);
    setPage(pageParam);
    if (refresh) setOrders([]);

    try {
      const results = await listOrdersApi(
        buildQuery({ sortParam, pageParam, searchParam, scopeParam, multiValueParam })
      );
      if (!mountedRef.current || API_ID !== apiIdRef.current) return;

      setOrders((prev) => [...(refresh ? [] : prev), ...results]);
      setLoading(false);
      setMore(results.length >= PER_PAGE);
    } catch (error) {
      console.log(error);
      if (!mountedRef.current || API_ID !== apiIdRef.current) return;
      setLoading(false);
      setOrders([]);
      setPage(1);
      setMore(false);
    }
  };

  const onSort = (newSort) => getOrders({ sortParam: newSort, refresh: true, pageParam: 1 });


  const onStatusChange = async (orderId, status) => {
    await updateOrderApi(orderId, { status });
    setOrders((prev) =>
      prev.map((order) => (order._id === orderId ? { ...order, status } : order))
    );
  };

  const onMultiSelect = (data) => {
    setMultiValue(data);
    getOrders({ refresh: true, pageParam: 1, multiValueParam: data });
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleSearchSubmit = () => {
    getOrders({ refresh: true, pageParam: 1 });
  };

  const handleScopeChange = (newScope) => {
    setScope(newScope);
    getOrders({ refresh: true, pageParam: 1, scopeParam: newScope });
  };

  const loadMore = () => {
    if (loading || !more) return;
    getOrders({ pageParam: page + 1 });
  };

  return (
    <SearchLayout
      topBar={
        <OrdersTopBar
          searchValue={search}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          isLoading={loading}
        />
      }
      sidebar={
        <OrdersSidebar
          statusOptions={statusOptions}
          statusValue={multiValue}
          onStatusChange={onMultiSelect}
          scope={scope}
          onScopeChange={handleScopeChange}
        />
      }
    >
      <ContentContainer>
        <InfiniteList onLoad={loadMore} hasMore={more} initialLoad={false}>
          {loading && !orders.length ? (
            ''
          ) : (
            <OrdersTable
              sort={sort}
              rows={orders}
              onSort={onSort}
              onStatusChange={onStatusChange}
            />
          )}
        </InfiniteList>
      </ContentContainer>
    </SearchLayout>
  );
};

export default OrdersContainer;
